/*
 * Client for the minimal my-api (services/my-api — allocation / invite /
 * claim, OnePulse slice 1). Base URL comes from VITE_MY_API_BASE per the
 * docs/MY-API-CONTRACT.md convention: unset ⇒ callers render an honest
 * unconfigured state; nothing is mocked.
 */

import { normalizeEmail } from '../auth/normalizeEmail'
import { createFifoLimiter } from './fetchLimiter'

export type HolderState = 'invited' | 'claimed' | 'ready' | 'consumed' | 'revoked'

export interface AllocationHolder {
  holderId: string
  state: HolderState
  allocatedEmail: string | null
  invitedAt: string
  inviteEmailSentAt: string | null
}

export interface OrderAllocations {
  orderId: string
  orderRef: string
  eventId: string
  eventName: string
  /** EVENT DATE CARRY (BOARDROOM-2026-09-01-175306 items 1–2; proposal
   *  MY-2026-09-01-180752): ISO `events.starts_at` / `events.ends_at` off the
   *  my-api orders read. `eventEndsAt` anchors the Current/Past orders
   *  partition (isPastEvent, src/data/pastEvents.ts); `eventStartsAt` is the
   *  REAL race-day anchor for assign-flow band checks (raceDayFrom). Optional/
   *  null on an older my-api or a degraded server read — the FE then keeps the
   *  order in Current (never wrongly buried) and anchors bands to now. */
  eventStartsAt?: string | null
  eventEndsAt?: string | null
  /** `ticketUnitId` (BOARDROOM-2026-08-17-210222): the team unit the ticket
   *  belongs to — groups a Team-of-N unit's N real ticket rows into per-seat
   *  assign targets. Optional so a pre-deploy my-api (no field) degrades to
   *  the typeName-grouping fallback rather than erroring.
   *
   *  `holderComplete` / `qrAvailable` (TOLERANT READS — Duncan's corrected
   *  deferred-child model, BOARDROOM-2026-09-02-152856): purchaser-visible
   *  signals for a seat DEFERRED to the child's own parent (guestparent).
   *  `holderComplete` = the holder (the parent) has completed profile +
   *  waiver; `qrAvailable` = the child's QR may be shown in the PURCHASER's
   *  account too (the buyer takes the kids to the venue). BOTH ABSENT on
   *  today's my-api — the server leg lands them in parallel; absent/null =
   *  today's behaviour (waiting states only, NO done flip, NO QR). The
   *  load-bearing rule: NO signature → NO QR anywhere — the FE never renders
   *  a QR affordance for a deferred seat from local/optimistic state; only
   *  `qrAvailable === true` unlocks it. */
  tickets: Array<{
    ticketId: string
    ticketUnitId?: string | null
    typeName: string
    holder: AllocationHolder | null
    holderComplete?: boolean | null
    qrAvailable?: boolean | null
  }>
}

export interface ClaimLookup {
  state: HolderState
  eventName: string
  eventStartsAt: string | null
  venue: string | null
  ticketTypeName: string
  invitedByName: string | null
  allocatedEmailMasked: string
  /** GUARDIAN CLAIM (S1/S2 wire contract, BOARDROOM-2026-09-03-052111 /
   *  CMT-2026-09-03-020; camelCase per the S1 build note — every /claim wire
   *  field is camelCase, snake_case being reserved for bus emits//svc/DB
   *  payloads): 'guardian' ⇔ this invite asks a PARENT/GUARDIAN to claim and
   *  manage a CHILD's seat — the child stays the participant; the parent
   *  signs FOR them. ABSENT (older my-api) or 'self' = today's self-claim
   *  behaviour, byte-identical rendering. */
  claimKind?: 'guardian' | 'self'
  /** The CHILD's name on guardian rows (never fabricated client-side);
   *  null/absent otherwise. */
  childName?: string | null
}

/** Normalised guardian-claim context off a claim lookup: non-null ⇔ the row is
 *  a guardian claim (claimKind === 'guardian'). `childName` is '' when the
 *  wire carried no usable name — callers render the honest name-less variant,
 *  never an invented child. */
export function guardianClaimContext(lookup: ClaimLookup): { childName: string } | null {
  if (lookup.claimKind !== 'guardian') return null
  return { childName: (lookup.childName ?? '').trim() }
}

export interface ClaimResult {
  holderId: string
  ticketId: string
  eventId: string
  eventName: string
  ticketTypeName: string
  /** ADDITIVE guardian carry on the POST /claim result too (S1 build note,
   *  camelCase) — the FE prefers the result's own facts for the post-claim
   *  record when present; absent (older my-api) falls back to the lookup's. */
  claimKind?: 'guardian' | 'self'
  childName?: string | null
}

/** Holder kind on the /me/holdings wire (child-holder slice, BOARDROOM
 *  launch-gate dispatch): who the seat is actually FOR. Optional end-to-end —
 *  an older my-api omits it, and every consumer must then behave exactly as
 *  before the field existed (self-subject rendering, no child labels). */
export type HolderKind = 'self' | 'child' | 'invitee' | 'anonymous'

export interface Holding {
  ticketId: string
  holderId: string
  eventId: string
  eventName: string
  ticketTypeName: string
  state: HolderState
  /** Per-holding subject kind (child-holder slice). ABSENT on an older
   *  my-api — tolerated everywhere; absent reads as today's behaviour. */
  kind?: HolderKind
  /** The child's full name when kind='child'; null/absent otherwise (older
   *  my-api or a non-child holder). NEVER fabricated client-side. */
  holderName?: string | null
  /** GUARDIAN-CLAIM discriminator off /me/holdings (server: present ONLY on
   *  claim_kind='guardian' rows; the row's kind stays 'invitee'). Lets the
   *  Profile children list find guardian-claimed child seats server-side. */
  claimKind?: 'guardian'
  /** CHILD-CONTEXT carry off /me/holdings (boardroom ruling 2026-10-10):
   *  true when the SERVER's lane rule marks the seat a child seat (kind
   *  'child' OR claim_kind 'guardian' — the same rule its questionnaire
   *  subject check uses). Lets a guardian on a FRESH device (no local claim
   *  record) key the questionnaire on the child. ABSENT on plain rows and on
   *  an older my-api. */
  childContext?: boolean
  /** INVITEE ORG-BRANDING carry (MY-2026-09-10-192731): the holding's
   *  org-unit tenancy key (orders.org_unit_id server-side) so branding
   *  resolves for holdings the caller did NOT purchase. ABSENT on an older
   *  my-api or when the server could not resolve it — the neutral band
   *  renders honestly, exactly as before the field existed. */
  orgUnitId?: string | null
  /** MINTED-BIB carry (BOARDROOM-2026-09-11-131819): display-ready bib off
   *  /me/holdings (ticket_units.bib_number, mint-width padded server-side) —
   *  the source that reaches ASSIGNED holders, who have no box-office orders
   *  read of their own. ABSENT on an older my-api / unminted seat — the
   *  wallet keeps its box-office fallback then em-dash, exactly as before. */
  bib?: string | null
}

/** Per-event heat policy as emitted by my-api's `event_heat_policy_v1` fold
 *  (CMT-2026-08-10-018). The view spells the allocated case `system-allocate`;
 *  the FE `HeatPolicy` type spells it `system-allocated` — `heatPolicyFromReadiness`
 *  in getReady.ts bridges the two. 'box-office' (P1 BOARDROOM-2026-09-09-
 *  171256): the buyer picked the heat at CHECKOUT — a third mode the settings
 *  view is being asked to emit (cross-entity ask via boardroom); my-api
 *  already passes the string through verbatim (raw store read), so this FE
 *  support lands deploy-ahead and lights up the moment settings emits it. */
export type HeatPolicyValue = 'self-select' | 'system-allocate' | 'box-office'

export interface Readiness {
  /** Spectator ruling (amendment_spectator_1019): a 'spectator' ticket needs
   *  profile + waiver only; the questionnaire, heat and start-time steps are
   *  hidden. Absent (older my-api) = standard. */
  ticketKind?: 'standard' | 'spectator'
  profileComplete: boolean
  waiverComplete: boolean
  heatComplete: boolean
  ready: boolean
  /** Per-event heat policy (CMT-2026-08-10-018). Optional/`null` when the server
   *  sends no signal — an older my-api build, or the settings view not yet
   *  applied — in which case the FE keeps its self-select default. */
  heatPolicy?: HeatPolicyValue | null
  /** `true` self-select · `false` system-allocate · absent/`null` no signal. */
  heatSel?: boolean | null
  /** ISO event start (nullable) — the chronological sort key for the get-ready
   *  home cards (CMT-121335). Optional: older my-api builds omit it, in which
   *  case the FE sort leaves those cards in stable order (degrades gracefully)
   *  until the my-api getReadiness change that emits it deploys. */
  eventStartsAt?: string | null
  /** The caller's CURRENTLY SELECTED heat start time as "HH:mm" (CMT-121325).
   *  Optional/`null` when no heat is chosen, or an older my-api that does not
   *  emit it — the wallet ticket then falls back to the box-office wave time. */
  selectedHeatTime?: string | null
  /** POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): SERVER-computed "the
   *  event has ended" (events.ends_at vs now, computed my-api-side at read
   *  time — the FE must NEVER re-derive clock logic). Drives the closed-state
   *  card (dim + "Event ended" badge + sort sink) and the gated assign/step
   *  CTAs. Optional: absent (older my-api) or false = event open. */
  eventClosed?: boolean
  /** ELIGIBILITY CARRY (BOARDROOM-2026-08-28-194055 item 4): the ticket's R1
   *  eligibility contract off the readiness read — the INVITEE's first in-hand
   *  contract (their Claim/Holding payloads never carried one; only the
   *  purchaser wire did). Feeds the ProfileWizard pre-save gate. `null` =
   *  honestly unconfigured/degraded server-side; absent = older my-api. Either
   *  way the FE gate skips — never a guessed restriction. */
  eligibility?: { gender: 'M' | 'F' | 'BOTH' | null; ageMin: number | null; ageMax: number | null } | null
  /** ISO race-day anchor for the age band (events.starts_at — the SAME anchor
   *  the server gate uses). Absent/null → the FE age axis is skipped. */
  raceDay?: string | null
  /** PAST-EVENT PARTITION carry (BOARDROOM-2026-09-01-175306 item 2): the RAW
   *  ISO `events.ends_at` instant off the readiness reads — the Tickets badge,
   *  wallet and Get-ready home exclude settled past events via the ONE shared
   *  boundary (isPastEvent + PAST_SETTLE_GRACE_MS, src/data/pastEvents.ts).
   *  Absent/null (older my-api, degraded read, kill-switch) ⇒ the event stays
   *  visible — never wrongly buried. */
  eventEndsAt?: string | null
  /** GUARDIAN CHILD DETAILS (my-api V25): present ONLY on a guardian-claimed
   *  seat — true once the child's name, DOB and gender are stored server-side
   *  (POST /me/tickets/{id}/guardian-child), false while the parent still has
   *  to confirm them (profileComplete is then false too). SERVER TRUTH for the
   *  childConfirm step; absent (non-guardian seat / older my-api) ⇒ the FE
   *  falls back to its device-local guardian record. */
  childDetailsComplete?: boolean
  /** my-api V25 AGE-BAND SPLIT (ruling 1 on #241), guardian seats only: true
   *  when the missing child details GATE the seat (a restricted ticket: age
   *  band OR gender restriction) — the childConfirm step is then a required
   *  to-do. false on an unrestricted ticket (no band, no gender):
   *  the step is a NON-blocking prompt (readiness/QR unaffected). Absent
   *  (older my-api) ⇒ treated as blocking (the pre-split behaviour). */
  childDetailsBlocking?: boolean
  /** TEEN SELF-HOLDER (my-api MY_TEEN_SELF_HOLDER_ENABLED, PR1): true ONLY on
   *  a kind='self' row declared 'minor' with no guardian-signed acceptance yet
   *  — the waiver step is "Parent/guardian signature needed". Absent
   *  otherwise (spread-omit). */
  guardianSignatureRequired?: true
  /** The latest guardian request (my-api, flag on: present on a minor self
   *  row; state 'none' before any request). Server truth for the card on any
   *  device; absent (older my-api / read failure) ⇒ the device-local record. */
  guardianRequest?: GuardianRequestView | null
  /** my-api: present (true) only when the profile is otherwise complete and the
   *  event's required fitness_self question is the sole outstanding item. The
   *  profile row then says "Answer your fitness level" and Start opens the
   *  wizard's fitness step. Absent ⇒ the generic "Details missing" row. */
  profileBlockedOnlyByFitness?: boolean
  /** OTHER QUESTIONS — INFORMATIONAL (boardroom ruling 2026-10-20 option 2,
   *  PROGRAMME-RULE-SELF-SERVICE-COMPLETE-DEFINITION-V1): does the event emit
   *  any REQUIRED non-fitness questionnaire question for this participant?
   *  Read my-api-side from the same settings /resolved body as the fitness
   *  re-derive. null = unknown (settings unreachable) or an exempt seat
   *  (child / guardian / spectator); absent = older my-api. Never part of
   *  `ready` / the QR. */
  otherQuestionsRequired?: boolean | null
  /** true = every required non-fitness question answered; false = at least
   *  one unanswered; null/absent = unknown (as above). Informational only. */
  otherQuestionsComplete?: boolean | null
}

export interface HeatOption {
  heatId: string
  label: string
  displayOrder: number
  // Canon heat-selection enrichment (D18) — optional so pre-extension
  // payloads/fixtures still typecheck; the UI renders only what is present
  // and never fabricates a time/capacity.
  /** Wall-clock start on the event day, "HH:MM" (canon bold "12:45"). */
  startTime?: string | null
  /** Canon "Wave {n}" ordinal. */
  waveNumber?: number | null
  /** Max participants (canon "{x} people" / "{left} OF {cap} LEFT"). */
  capacity?: number | null
  /** Live count of participants currently assigned to this heat. */
  assignedCount?: number | null
  /** capacity - assignedCount; null/absent when capacity is unknown. */
  spotsRemaining?: number | null
}

/** Caller-scoped header context returned with the type-scoped heats read
 *  (canon D15/D16 + free re-selection preselect). */
export interface HeatListContext {
  eventId: string
  eventName: string
  eventStartsAt: string | null
  ticketTypeId: string | null
  ticketTypeName: string | null
  /** The caller's ticket's current heat, if any — re-selection is FREE for a
   *  solo ticket. */
  assignedHeatId: string | null
  /** TEAM ticket = the caller's unit has >1 member; the team shares ONE heat. */
  isTeam?: boolean
  /** FIRST-PICKER-WINS lock (BOARDROOM-230739): true when this is a team whose
   *  heat is already set — the caller inherits it read-only and cannot move the
   *  team. Optional so a pre-deploy my-api (no field) degrades to the old free
   *  picker rather than erroring. */
  unitLocked?: boolean
}

export interface HeatCatalog {
  heats: HeatOption[]
  context: HeatListContext | null
}

export interface WaiverInput {
  signerName: string
  consented: boolean
  templateVersion: string
  templateHash: string
  signedAt: string
}

export type QrStatus = 'active' | 'consumed' | 'revoked'

/** Legacy account marry-up (CMT-035, Approach B = JIT link-on-login). The
 *  pre-fillable profile the my-api `/me/legacy-profile` endpoint returns on a
 *  legacy match. EVERY field is nullable — the datalake may hold only some, and
 *  a NULL is an honest "not present", never a fabricated placeholder. Shapes
 *  mirror the wizard's field homes (birthdate `YYYY-MM-DD`, phone E.164 where
 *  known) so `draftFromLegacy` is a straight transcription. */
export interface LegacyProfile {
  givenName: string | null
  familyName: string | null
  birthdate: string | null
  gender: string | null
  phone: string | null
  nationality: string | null
  address: string | null
  city: string | null
}

export interface LegacyProfileResult {
  linked: boolean
  profile: LegacyProfile | null
}

/** Identity-field verification level (LOCKED rule PROGRAMME-RULE-IDENTITY-
 *  FIELD-EDIT-LOCK-BY-VERIFICATION-LEVEL-V1; my scope per BOARDROOM-2026-08-
 *  29-114614): 3 = self-attested (default — free self-edit), 2 = event-ID-
 *  verified at check-in (identity fields not self-editable), 1 = third-party-
 *  verified (locked for everyone). Read-only on this surface — the promotion
 *  write (L3→L2) and the CS edit path live with other entities. */
export type IdentityVerificationLevel = 1 | 2 | 3

export function myApiBase(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_MY_API_BASE || null
}

function url(base: string, path: string): string {
  return `${base.replace(/\/$/, '')}${path}`
}

async function check<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    let code = ''
    let reason: string | undefined
    let serverMessage: string | undefined
    let rawBody: Record<string, unknown> | undefined
    try {
      const body = (await res.json()) as { error?: string; reason?: string; message?: string }
      if (body && typeof body === 'object') rawBody = body as Record<string, unknown>
      code = body.error ?? ''
      // Eligibility 422s (BOARDROOM-2026-08-28-194055) carry a machine `reason`
      // (gender|age) + the server's plain-English `message` — captured so the
      // FE can surface the exact reject per ticket and map copy per locale.
      reason = typeof body.reason === 'string' ? body.reason : undefined
      serverMessage = typeof body.message === 'string' ? body.message : undefined
    } catch {
      /* body not json */
    }
    const err = new MyApiError(`${what} failed (${res.status})`, res.status, code, reason, serverMessage)
    // The whole error body rides NON-ENUMERABLE (teen self-holder PR3: the
    // 422's `next`, a 429's `retryAt` / `retry_after`), so every existing
    // equality / serialisation of a MyApiError is unchanged.
    if (rawBody) Object.defineProperty(err, 'body', { value: rawBody, enumerable: false })
    throw err
  }
  return (await res.json()) as T
}

/** PUT /me/phone: my-api validates the account phone and writes it to
 *  Cognito as E.164. Resolves to the stored E.164; throws MyApiError on 4xx/5xx
 *  (400 phone_invalid / phone_required, 503 phone_unavailable). */
export async function putAccountPhone(
  idToken: string,
  base: string,
  phone: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  const res = await fetchImpl(url(base, '/me/phone'), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ phone }),
  })
  return (await check<{ phone: string }>(res, 'save phone')).phone
}

export class MyApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    /** Machine axis on an eligibility 422 (gender|age) — absent otherwise. */
    public reason?: string,
    /** The server's plain-English reject copy — the honest fallback when no
     *  locale mapping applies. NEVER fabricated client-side. */
    public serverMessage?: string,
  ) {
    super(message)
  }
  /** The parsed error body, when JSON (non-enumerable — set by check()). */
  declare readonly body?: Record<string, unknown>
}

/** Max concurrent GET /me/orders/{id}/allocations requests
 *  (BOARDROOM-2026-09-01-225419 item 2). 6 because (a) browsers historically
 *  cap ~6 HTTP/1.1 connections per origin, so a larger fan-out buys nothing on
 *  the wire, and (b) 6 stays comfortably inside API-gateway/Lambda burst
 *  limits — the 241-card burst is what rate-limited/500'd and degraded every
 *  card at once. */
export const ALLOCATIONS_FETCH_LIMIT = 6

/** ONE limiter shared by every fetchOrderAllocations caller (each OrderCard's
 *  useOrderSeats, refresh(), single-card views) — that sharing is the point:
 *  the bound is per-app, not per-card. */
const allocationsLimiter = createFifoLimiter(ALLOCATIONS_FETCH_LIMIT)

/** Bounded to ALLOCATIONS_FETCH_LIMIT concurrent requests via the shared FIFO
 *  limiter above — with N order cards mounted, at most 6 GETs are in flight
 *  and the rest queue in mount order. The limited task spans the request AND
 *  the body parse (the slot models the whole connection) and captures only
 *  this call's four small arguments (WU6 scope lesson). The slot is released
 *  on failure too (check() throws inside the task), and the useOrderSeats
 *  retry/backoff (PR#103) calls this function afresh per attempt — every
 *  retry RE-ACQUIRES a slot, never bypasses the limiter. */
export async function fetchOrderAllocations(
  idToken: string,
  base: string,
  orderId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<OrderAllocations> {
  return allocationsLimiter(async () => {
    const res = await fetchImpl(url(base, `/me/orders/${orderId}/allocations`), {
      headers: { Authorization: `Bearer ${idToken}` },
    })
    return check(res, 'allocations fetch')
  })
}

/** Guestparent parent-permission ask (Duncan order BOARDROOM-2026-09-02-150714
 *  / comms dispatch BOARDROOM-150801): when the allocation is a request for a
 *  PARENT to sign for a child, pass their names so the server emits the
 *  dedicated parent_sign_request letter greeting the PARENT (never the
 *  allocator, never the child). Omit for a plain allocation — the wire body
 *  is then byte-identical to today. */
export interface ParentSignInput {
  /** Optional: blank/whitespace is omitted from the wire (the server also
   *  treats "" as absent). */
  parentName: string
  childName: string
}

/** The wire form of the assign flow's under/over-18 declaration (GATE 4
 *  declaration parity, d20a — BOARDROOM-2026-09-08-134617). Mirrors the UI's
 *  own `Bracket` vocabulary (assignModel): 'adult' = declared 18-or-over
 *  (incl. the fully-adult band where the bracket step is skipped because the
 *  ticket type itself admits only adults), 'minor' = declared under-18 — the
 *  allocation then MUST carry parent_sign (the permission-to-sign the UI
 *  collects) and rides the server's guardian lane. */
export type DeclaredBracket = 'adult' | 'minor'

export async function allocateTicket(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  email: string,
  // REQUIRED since GATE 4 (d20a): the allocation API now refuses an invite
  // with no age declaration (422 ineligible/declaration_required) — the FE
  // and API ship the contract together. CACHE-WINDOW RISK (deliberate,
  // documented): an older cached bundle without this field 4xxs on
  // allocation until the user refreshes; NO server-side grace fallback
  // exists — the ruling says REQUIRES — and the server's honest message
  // tells the user to refresh.
  bracket: DeclaredBracket,
  fetchImpl: typeof fetch = fetch,
  parentSign?: ParentSignInput,
): Promise<{ holderId: string; state: HolderState; allocatedEmail: string; inviteEmailSent: boolean }> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/allocation`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    // Invitee email is normalised at the boundary — it becomes the invited
    // Cognito identity downstream (BOARDROOM-2026-08-24-183930 #50 FIX A).
    // parent_sign rides only when supplied (spread-omit) — required by the
    // server exactly when bracket='minor' (the UI's minor routes always
    // collect it).
    body: JSON.stringify({
      email: normalizeEmail(email),
      bracket,
      ...(parentSign
        ? {
            parent_sign: {
              ...(parentSign.parentName.trim() ? { parentName: parentSign.parentName } : {}),
              childName: parentSign.childName,
            },
          }
        : {}),
    }),
  })
  return check(res, 'allocation')
}

/** Child-holder wire input (launch-gate slice; contract fixed with the my-api
 *  leg). `dob` is 'YYYY-MM-DD'; the contract requires it, but the FE types it
 *  nullable so an (unreachable-today: childDob is Required) DOB-less record
 *  rides as an honest null and the SERVER's 422 is the reject surface — the
 *  client never fabricates a date. `gender` is the wire vocabulary 'M'|'F',
 *  null for unknown / self-described (the server's gender gate conservative-
 *  allows null, mirroring the FE's own genderEligible posture). */
export interface ChildHolderInput {
  child: {
    firstName: string
    lastName: string
    dob: string | null
    gender: 'M' | 'F' | null
  }
  /** false = the purchaser's OWN child; true = someone else's child signed
   *  for under a stated permission (TWO POOLS, PRODUCT-RULES §Children). */
  guest: boolean
}

/** Create a CHILD holder on a purchased seat (launch-gate slice: the child /
 *  guestchild / teen-MINE confirms become server-backed). Same error surface
 *  as allocateTicket: eligibility 422s carry {reason:'age'|'gender'|
 *  'duplicate_participant', message}; a live holder is 409/422
 *  already_allocated; a closed event is 409 event_ended. 201 returns the new
 *  holderId plus the seat's lean readiness. */
export async function createChildHolder(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  input: ChildHolderInput,
  fetchImpl: typeof fetch = fetch,
): Promise<{ holderId: string; readiness: Readiness }> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/child-holder`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check(res, 'child-holder')
}

/** The child's details as the guardian confirms them (ChildConfirm). `dob` =
 *  'YYYY-MM-DD'; `gender` = 'M' | 'F' (required by the server). */
export interface GuardianChildInput {
  firstName: string
  /** OPTIONAL (ruling 5 on #241 — single-word names are legitimate): omit
   *  when the child has no last name; the server stores NULL. */
  lastName?: string
  dob: string
  gender: 'M' | 'F'
}

/** The server error code a guardian seat's profile-complete call returns (422)
 *  while the child's details are not stored yet — the FE routes the parent to
 *  the ChildConfirm step (stepEntryPath 'childConfirm'), never a dead end. */
export const CHILD_DETAILS_REQUIRED = 'child_details_required'

/** Store the child's details on a guardian-claimed seat (my-api V25). The
 *  server validates the child against the ticket's age band + gender on race
 *  day BEFORE writing: a mismatch is a 422 `ineligible` {reason:'age'|'gender',
 *  message} (or `age_unverifiable`), exactly like child-holder creation; a
 *  malformed body is 400 `invalid_input`; a seat that is not a guardian seat is
 *  403 `not_guardian_holder`; a closed event 409 `event_ended`. 200 returns the
 *  seat's readiness with `childDetailsComplete: true`. Errors surface as
 *  MyApiError (status/code/reason/serverMessage) via the shared `check`. */
export async function submitGuardianChild(
  idToken: string,
  base: string,
  ticketId: string,
  input: GuardianChildInput,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/guardian-child`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check(res, 'guardian-child')
}

/** The child lane of a seat (server-backed child flow, boardroom 2026-10-01):
 *  a kind='child' seat (assigned to the child; identity captured at assign)
 *  or a claim_kind='guardian' seat (claimed by the parent for the child). */
export type ChildLane = 'child' | 'guardian'

/** GET /me/tickets/{id}/child-details — the child identity the SERVER holds
 *  for a child seat (both lanes). Honest nulls; `childName` is the purchaser-
 *  typed seed on a not-yet-confirmed guardian seat (prefill only). `kind` /
 *  `childName` are tolerated absent. */
export interface ChildDetails {
  firstName: string | null
  lastName: string | null
  dob: string | null
  gender: 'M' | 'F' | null
  complete: boolean
  kind?: ChildLane
  childName?: string | null
}

/** Read the child's details for a child seat, keyed by TICKET (never a
 *  device-local record). 404 `no_live_holder` / 403 `not_guardian_holder`
 *  surface as MyApiError. */
export async function fetchChildDetails(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ChildDetails> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/child-details`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'child-details fetch')
}

/** Store/edit the child's details on a child seat — BOTH lanes (POST
 *  /me/tickets/{id}/child-details; /guardian-child is the server-side alias).
 *  Same validation + error shapes as submitGuardianChild; a kind='child' seat
 *  additionally requires `lastName` (400 invalid_input without it). */
export async function submitChildDetails(
  idToken: string,
  base: string,
  ticketId: string,
  input: GuardianChildInput,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/child-details`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  // DEPLOY-ORDER FALLBACK (boardroom 2026-10-01): my-api and the FE ship
  // separately (stage pipelines, prod releases), so this FE can meet an older
  // my-api without the /child-details path. Such a my-api answers its
  // catch-all 404 {error:'not_found'} — distinct from this route's own 404
  // `no_live_holder`. Only then, retry ONCE on the pre-existing /guardian-child
  // path (guardian seats save; a kind='child' seat gets that older server's
  // honest 403 not_guardian_holder).
  if (res.status === 404 && (await routeMissing(res))) {
    return submitGuardianChild(idToken, base, ticketId, input, fetchImpl)
  }
  return check(res, 'child-details')
}

/** true ⇔ a 404 is my-api's catch-all unknown-route response (an older
 *  my-api without the route), not a route's own 404 code. Reads a CLONE so
 *  the original body stays available to `check`. */
async function routeMissing(res: Response): Promise<boolean> {
  try {
    const body = (await (res.clone?.() ?? res).json()) as { error?: unknown }
    return body?.error === 'not_found'
  } catch {
    return false
  }
}

export async function revokeAllocation(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/allocation/revoke`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  await check(res, 'revoke')
}

// ---------------------------------------------------------------------------
// Holdings + get-ready (WU-2/WU-3, dispatch BOARDROOM-2026-08-07-233226)
// ---------------------------------------------------------------------------

/** Actor TYPE on a removed-holdings entry (build item #6, BOARDROOM-2026-09-
 *  05-121056): WHO caused the change — the ONLY actor fact the FE ever
 *  renders. Recipient identity is NEVER on this type (privacy default:
 *  "transferred to another participant", actor type only). */
export type RemovedHoldingActorType = 'owner' | 'support' | 'system'

/** One holding this account USED TO have (transferred away / reassigned /
 *  CS-changed) — the ADDITIVE optional `removedHoldings[]` carry on the
 *  /me/holdings wire (server leg lands in parallel; absent = today's my-api =
 *  today's behaviour). Exactly the five display facts and NOTHING else — the
 *  parse seam below strips any other field, so recipient identity can never
 *  even reach the render layer. */
export interface RemovedHolding {
  eventName: string
  ticketTypeName: string | null
  /** What happened as the wire words it ('transferred' | 'reassigned' |
   *  'claimed-elsewhere' | 'revoked' | …). Owner-line display copy is
   *  change-verb-aware (review fix F2a): only a transfer-shaped verb claims a
   *  transfer; 'revoked'/unknown reads "removed by the order owner". */
  change: string | null
  /** ISO instant of the change; null/unparseable ⇒ rendered without a date
   *  (honest absence, never a fabricated one). Rendered UTC-anchored
   *  (shortChangeDate, review fix F3) to match the server audit log. */
  occurredAt: string | null
  /** Out-of-domain/absent reads null → the actor-SILENT "{head} — changed on
   *  {date}" phrasing (review fix F2b) — never a guessed actor, and never
   *  "updated automatically" (that asserts a system actor). */
  actorType: RemovedHoldingActorType | null
}

const nonEmptyString = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v.trim() : null

/** TOLERANT parse of the raw wire field. The contract is RECONCILED with the
 *  my-api leg (review fix F6 — the earlier speculative spelling aliases
 *  removedAt|at|when / action|what are gone): the field names are exactly
 *  eventName / ticketTypeName / change / occurredAt / actorType, and the
 *  server feed is recency-bounded (90 days, newest 10) and excludes
 *  live-held tickets — wire shape unchanged. Tolerance is for MALFORMED data
 *  only, which degrades to absence: non-array → [], junk/event-less entries
 *  dropped, unknown actorType → null. Exported for the contract tests. */
export function normalizeRemovedHoldings(raw: unknown): RemovedHolding[] {
  if (!Array.isArray(raw)) return []
  const out: RemovedHolding[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const r = entry as Record<string, unknown>
    const eventName = nonEmptyString(r.eventName)
    if (!eventName) continue // a headless line ("— — transferred…") helps no one
    const actor = r.actorType
    out.push({
      eventName,
      ticketTypeName: nonEmptyString(r.ticketTypeName),
      change: nonEmptyString(r.change),
      occurredAt: nonEmptyString(r.occurredAt),
      actorType: actor === 'owner' || actor === 'support' || actor === 'system' ? actor : null,
    })
  }
  return out
}

export interface HoldingsRead {
  holdings: Holding[]
  /** [] on an older my-api (field absent) — every consumer then behaves
   *  exactly as before the field existed. */
  removedHoldings: RemovedHolding[]
}

/** Full /me/holdings read: live holdings + the removed-holdings carry. */
export async function fetchHoldingsRead(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<HoldingsRead> {
  const res = await fetchImpl(url(base, '/me/holdings'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  const body = await check<{ holdings: Holding[]; removedHoldings?: unknown }>(res, 'holdings fetch')
  return { holdings: body.holdings, removedHoldings: normalizeRemovedHoldings(body.removedHoldings) }
}

export async function fetchMyHoldings(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Holding[]> {
  return (await fetchHoldingsRead(idToken, base, fetchImpl)).holdings
}

/** Legacy account marry-up (CMT-035): fetch the caller's legacy profile for
 *  pre-fill. The endpoint keys on the caller's VERIFIED token email (never a
 *  body param — no PII in the request), links `users.legacy_object_id` on a
 *  match (append-only/idempotent, server-side), and returns { linked, profile }.
 *  No match / pending datalake lookup ⇒ { linked:false, profile:null }; the
 *  endpoint degrades to that on any failure, so this never blocks login. */
export async function fetchLegacyProfile(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LegacyProfileResult> {
  const res = await fetchImpl(url(base, '/me/legacy-profile'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'legacy profile fetch')
}

/** Fetch the caller's identity-field verification level from the my-api
 *  user-scoped read (`GET /me/identity-verification` — new small route per the
 *  dispatch: the existing me-scoped reads are holding-scoped (/me/holdings,
 *  /me/readiness are per-ticket and EMPTY for a zero-holding user) or
 *  side-effectful (/me/legacy-profile links on call), while the level is
 *  USER-scoped and must be readable with zero holdings).
 *
 *  TOLERANT PARSE: only an explicit 1 or 2 locks — anything else (absent
 *  field, out-of-domain value) reads 3, the rule's default. Fetch/HTTP
 *  failures THROW (MyApiError) so the caller decides; the profile surface
 *  fails OPEN to 3 (see profileStore.loadIdentityVerificationLevel) — never
 *  lock a user out of their own profile on a read failure. */
export async function fetchIdentityVerificationLevel(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<IdentityVerificationLevel> {
  const res = await fetchImpl(url(base, '/me/identity-verification'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  const { level } = await check<{ level: unknown }>(res, 'identity-verification fetch')
  return level === 1 || level === 2 ? level : 3
}

/** Legacy customer history (WU PROJ-0062-WU-LEGACY-HISTORY-01; LOCKED
 *  PROGRAMME-DEC-LEGACY-CUSTOMER-MIGRATION-V1 sinks 2/5) — the wire shape of
 *  my-api `GET /me/history` (services/my-api/src/legacy-overview.ts). Spend is
 *  GROSS receipts per currency in minor units (refunds NOT netted, ruling 1),
 *  never converted or summed across currencies. Dates are plain YYYY-MM-DD. */
export type LegacyStatus = 'linked' | 'not_linked' | 'unknown'

export interface LegacyCurrencyTotal {
  /** ISO 4217 code. */
  currency: string
  spend_gross_minor: number
}

/** The six converted targets (contract amendment 02, schema v2). */
export const CONVERTED_CURRENCIES = ['USD', 'GBP', 'EUR', 'AED', 'SAR', 'THB'] as const
export type ConvertedCurrency = (typeof CONVERTED_CURRENCIES)[number]

export interface MyLegacyHistory {
  has_activity: boolean
  totals_by_currency: LegacyCurrencyTotal[]
  orders_count: number
  tickets_purchased: number
  events_attended: number
  results_count: number
  first_event_date: string | null
  last_event_date: string | null
  refunds_netted: false
  /** Schema v2 only (optional; older servers omit it): the gross spend
   *  converted to each target at the rate on each purchase date, in minor
   *  units (exponent 2), summed over every currency. null = not available
   *  (v1 data, or malformed) — never a partial total. */
  converted?: Record<ConvertedCurrency, number> | null
  /** Which converted total to show (profile residence's currency, else USD). */
  display_currency?: ConvertedCurrency
}

export interface MyCurrentActivity {
  orders_count: number
  events_attended: number
  upcoming_count: number
}

export interface MyHistory {
  legacy_status: LegacyStatus
  legacy: MyLegacyHistory | null
  current: MyCurrentActivity | null
}

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0

function isPlainDate(v: unknown): v is string {
  if (typeof v !== 'string') return false
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (!m) return false
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toISOString().slice(0, 10) === v
}

function parseLegacy(raw: unknown): MyLegacyHistory | null | undefined {
  if (raw === null) return null
  if (typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const l = raw as Record<string, unknown>
  if (typeof l.has_activity !== 'boolean') return undefined
  // Ruling 1: the summary is gross. Anything but an explicit false would make
  // the "Total paid" label unverifiable — refuse rather than mislabel.
  if (l.refunds_netted !== false) return undefined
  if (!Array.isArray(l.totals_by_currency)) return undefined
  const totals: LegacyCurrencyTotal[] = []
  for (const t of l.totals_by_currency as unknown[]) {
    if (typeof t !== 'object' || t === null) return undefined
    const { currency, spend_gross_minor } = t as Record<string, unknown>
    if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency) || !isCount(spend_gross_minor)) return undefined
    totals.push({ currency, spend_gross_minor })
  }
  const counts = ['orders_count', 'tickets_purchased', 'events_attended', 'results_count'] as const
  if (!counts.every((k) => isCount(l[k]))) return undefined
  const dates = ['first_event_date', 'last_event_date'] as const
  if (!dates.every((k) => l[k] === null || isPlainDate(l[k]))) return undefined
  // Converted totals are ADDITIVE optional fields: anything malformed drops
  // just them (the per-currency history still renders), never the block.
  let converted: Record<ConvertedCurrency, number> | null = null
  let displayCurrency: ConvertedCurrency | undefined
  const isTarget = (v: unknown): v is ConvertedCurrency =>
    typeof v === 'string' && (CONVERTED_CURRENCIES as readonly string[]).includes(v)
  if (isTarget(l.display_currency) && typeof l.converted === 'object' && l.converted !== null && !Array.isArray(l.converted)) {
    const c = l.converted as Record<string, unknown>
    if (CONVERTED_CURRENCIES.every((k) => isCount(c[k]))) {
      converted = Object.fromEntries(CONVERTED_CURRENCIES.map((k) => [k, c[k] as number])) as Record<ConvertedCurrency, number>
      displayCurrency = l.display_currency
    }
  }
  return {
    has_activity: l.has_activity,
    totals_by_currency: totals,
    orders_count: l.orders_count as number,
    tickets_purchased: l.tickets_purchased as number,
    events_attended: l.events_attended as number,
    results_count: l.results_count as number,
    first_event_date: l.first_event_date as string | null,
    last_event_date: l.last_event_date as string | null,
    refunds_netted: false,
    converted,
    ...(displayCurrency ? { display_currency: displayCurrency } : {}),
  }
}

function parseCurrent(raw: unknown): MyCurrentActivity | null {
  if (typeof raw !== 'object' || raw === null) return null
  const c = raw as Record<string, unknown>
  if (!isCount(c.orders_count) || !isCount(c.events_attended) || !isCount(c.upcoming_count)) return null
  return { orders_count: c.orders_count, events_attended: c.events_attended, upcoming_count: c.upcoming_count }
}

/** DEFENSIVE parse of a `GET /me/history` body. Unknown status or a malformed
 *  `legacy` block ⇒ null (render nothing). A malformed `current` block only
 *  nulls `current` (never fabricated; nothing renders from it today). */
export function parseMyHistory(raw: unknown): MyHistory | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const b = raw as Record<string, unknown>
  const status = b.legacy_status
  if (status !== 'linked' && status !== 'not_linked' && status !== 'unknown') return null
  const legacy = parseLegacy(b.legacy ?? null)
  if (legacy === undefined) return null
  return { legacy_status: status, legacy, current: parseCurrent(b.current) }
}

/** GET /me/history — NEVER throws. The route is new (not on stage/prod yet),
 *  so every failure (network error, any non-200 incl. 404/503, non-JSON or
 *  malformed body) degrades to null and the caller renders nothing extra —
 *  never an error banner. Keyed on the caller's VERIFIED token server-side. */
export async function fetchMyHistory(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<MyHistory | null> {
  try {
    const res = await fetchImpl(url(base, '/me/history'), {
      headers: { Authorization: `Bearer ${idToken}` },
    })
    if (res.status !== 200) return null
    return parseMyHistory(await res.json())
  } catch {
    return null
  }
}

/** Legacy SINK 3 per-event drill-down (backlog 1g; LOCKED PROGRAMME-DEC-
 *  LEGACY-CUSTOMER-MIGRATION-V1 clause 3, D1) — the wire shape of my-api
 *  `GET /me/history/events` (services/my-api/src/legacy-events.ts). Display
 *  fields only: no legacy, participant, event or org id is on this wire (the
 *  list is keyed by position). `status` is computed server-side with the
 *  precedence result > check-in > registration. */
export type LegacyEventStatus = 'finished' | 'did_not_finish' | 'took_part' | 'registered'

export interface MyLegacyEvent {
  event_name: string | null
  /** YYYY-MM-DD plain calendar date. */
  event_date: string | null
  event_end_date: string | null
  organisation_name: string | null
  org_unit_name: string | null
  category_name: string | null
  heat_name: string | null
  status: LegacyEventStatus
  finish_time_ms: number | null
  overall_rank: number | null
  field_size: number | null
  /** How many results the entry has; > 1 ⇒ "1 of N results" (the best is shown). */
  result_rows: number
}

/** ok = the list is authoritative (may be empty, incl. not linked);
 *  unavailable = any failure — the FE offers "Try again". */
export type MyLegacyEvents = { kind: 'ok'; events: MyLegacyEvent[] } | { kind: 'unavailable' }

const LEGACY_EVENT_STATUSES: readonly LegacyEventStatus[] = ['finished', 'did_not_finish', 'took_part', 'registered']
const optStr = (v: unknown): v is string | null => v === null || typeof v === 'string'
const optCount = (v: unknown): v is number | null => v === null || isCount(v)
const optDate = (v: unknown): v is string | null => v === null || isPlainDate(v)

function parseLegacyEvent(raw: unknown): MyLegacyEvent | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const e = raw as Record<string, unknown>
  const strs = ['event_name', 'organisation_name', 'org_unit_name', 'category_name', 'heat_name'] as const
  const counts = ['finish_time_ms', 'overall_rank', 'field_size'] as const
  if (!strs.every((k) => optStr(e[k] ?? null))) return null
  if (!counts.every((k) => optCount(e[k] ?? null))) return null
  if (!optDate(e.event_date ?? null) || !optDate(e.event_end_date ?? null)) return null
  if (typeof e.status !== 'string' || !(LEGACY_EVENT_STATUSES as readonly string[]).includes(e.status)) return null
  if (!isCount(e.result_rows)) return null
  const s = (k: (typeof strs)[number]) => (e[k] as string | null | undefined) ?? null
  const n = (k: (typeof counts)[number]) => (e[k] as number | null | undefined) ?? null
  return {
    event_name: s('event_name'),
    event_date: (e.event_date as string | null | undefined) ?? null,
    event_end_date: (e.event_end_date as string | null | undefined) ?? null,
    organisation_name: s('organisation_name'),
    org_unit_name: s('org_unit_name'),
    category_name: s('category_name'),
    heat_name: s('heat_name'),
    status: e.status as LegacyEventStatus,
    finish_time_ms: n('finish_time_ms'),
    overall_rank: n('overall_rank'),
    field_size: n('field_size'),
    result_rows: e.result_rows,
  }
}

/** DEFENSIVE parse of a `GET /me/history/events` body. A malformed item is
 *  dropped (the rest still render); a malformed envelope is unavailable. */
export function parseMyLegacyEvents(raw: unknown): MyLegacyEvents {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { kind: 'unavailable' }
  const b = raw as Record<string, unknown>
  if (b.status !== 'ok' || !Array.isArray(b.events)) return { kind: 'unavailable' }
  const events = (b.events as unknown[]).map(parseLegacyEvent).filter((e): e is MyLegacyEvent => e !== null)
  return { kind: 'ok', events }
}

/** GET /me/history/events — NEVER throws; called only on a tap. Any failure
 *  (network, non-200 incl. 404 flag-off, non-JSON, malformed) ⇒ unavailable. */
export async function fetchMyLegacyEvents(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<MyLegacyEvents> {
  try {
    const res = await fetchImpl(url(base, '/me/history/events'), {
      headers: { Authorization: `Bearer ${idToken}` },
    })
    if (res.status !== 200) return { kind: 'unavailable' }
    return parseMyLegacyEvents(await res.json())
  } catch {
    return { kind: 'unavailable' }
  }
}

/** The server participant profile (my-api V29 `participant_profiles`) — the
 *  Tier-1 personal record + emergency contact that used to live in device
 *  localStorage. Identity core (names / DOB / gender / phone number) stays in
 *  Cognito; fitness stays in settings — neither is on this wire. Every field
 *  is null when unset. */
export interface ServerProfile {
  nationality: string | null
  nationalityIso: string | null
  residenceCountry: string | null
  residenceIso: string | null
  city: string | null
  phoneDial: string | null
  aboutText: string | null
  shirtSize: string | null
  language: string | null
  ecName: string | null
  ecDial: string | null
  ecPhone: string | null
  ecRelationship: string | null
}

/** The profile as GET /me/profile returns it: the writable fields plus the
 *  SERVER-OWNED `cityFilledIn` (my-api V35; Duncan ruling 2026-10-07) — true =
 *  the city came from legacy data cleaning, so the app asks "Is {city} your
 *  nearest city?". Read-only: it is never part of a save body (the server
 *  ignores it); any save carrying `city` clears it. Absent on an older my-api
 *  (treated as false). */
export type ServerProfileWire = ServerProfile & { cityFilledIn?: boolean }

export interface ServerProfileRead {
  /** null = the account has no profile row yet (version 0). */
  profile: ServerProfileWire | null
  version: number
}

/** GET /me/profile. HTTP failures THROW MyApiError (status 404 = an older
 *  my-api without the route; 503 profile_store_unavailable = V29 not applied
 *  yet) — profileStore decides the degrade. */
export async function fetchMyProfile(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ServerProfileRead> {
  const res = await fetchImpl(url(base, '/me/profile'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  const body = await check<{ profile?: ServerProfileWire | null; version?: unknown }>(res, 'profile fetch')
  return {
    profile: body.profile ?? null,
    version: typeof body.version === 'number' ? body.version : 0,
  }
}

/** Write the profile (my-api PUT /me/profile — sent as its POST alias, because
 *  the API Gateway CORS allow-list carries GET/POST only). Present keys are
 *  written, absent keys left unchanged, null clears. `version` = the version
 *  read (0 = first insert). A stale version → MyApiError status 409 code
 *  'version_conflict'. */
export async function saveMyProfile(
  idToken: string,
  base: string,
  fields: Partial<ServerProfile>,
  version: number,
  fetchImpl: typeof fetch = fetch,
): Promise<{ version: number }> {
  const res = await fetchImpl(url(base, '/me/profile'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...fields, version }),
  })
  return check<{ version: number }>(res, 'profile save')
}

/** Delete MY Trumin account (LOCKED erasure ruling, WU PROJ-0062-WU-ERASURE-01).
 *  The server anonymises the account record only — orders, tickets and results
 *  are kept. Errors: 400 confirmation_required, 503 deletion_unavailable (nothing
 *  changed) / deletion_incomplete (safe to retry). */
export async function deleteMyAccount(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ deleted: true }> {
  const res = await fetchImpl(url(base, '/me/account/delete'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm: 'delete' }),
  })
  return check<{ deleted: true }>(res, 'account delete')
}

/** Get-or-create my own kind='self' holder for a ticket I purchased. */
export async function ensureSelfHolder(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
  /** TEEN SELF-HOLDER (flag on only): the "Myself" age answer → `{bracket}`.
   *  Absent ⇒ the request is byte-identical to before (no body). */
  opts?: { bracket?: 'adult' | 'minor' },
): Promise<{ holderId: string }> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/holder`), opts?.bracket
    ? {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ bracket: opts.bracket }),
      }
    : {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      })
  return check(res, 'holder bootstrap')
}

export async function fetchReadiness(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/readiness`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'readiness fetch')
}

/** Batched readiness for ALL of the caller's live holdings in ONE request
 *  (CMT-150431) — the get-ready home prefers this over the per-ticket N+1 that
 *  hung large orders (~18 tickets). A 404 means an older my-api WITHOUT the
 *  `/me/readiness` route; the caller catches it (MyApiError.status === 404) and
 *  falls back to the per-holding `fetchEventSteps` fan-out, so the FE degrades
 *  gracefully until the my-api change deploys. */
export async function fetchAllReadiness(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Array<{ ticketId: string; readiness: Readiness }>> {
  const res = await fetchImpl(url(base, '/me/readiness'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return (
    await check<{ readiness: Array<{ ticketId: string; readiness: Readiness }> }>(res, 'batched readiness fetch')
  ).readiness
}

/** Records profile completion on the server for a held ticket (flips
 *  `ticket_holders.profile_complete`). Called by getReady.ts `onProfileSaved`
 *  when the wizard saves a COMPLETE profile.
 *
 *  NOTE (Ruling C tension, BOARDROOM-2026-08-09-133534): Ruling C retired this
 *  call, intending the server to DERIVE profile readiness. That derivation was
 *  never implemented — my-api holds no participant-profile data (it lives in
 *  Cognito), so this endpoint is the ONLY thing that can set the flag, and
 *  nothing calling it left the get-ready profile step permanently "missing"
 *  (the CMT-043 live-test bug). Re-wired as a launch necessity; a Ruling C
 *  amendment should confirm this seam (or replace it with a server-side
 *  participant-profile store that derives completion). See onProfileSaved. */
export async function markProfileComplete(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/readiness/profile-complete`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'profile-complete')
}

export async function submitWaiver(
  idToken: string,
  base: string,
  ticketId: string,
  input: WaiverInput,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/waiver`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check(res, 'waiver submit')
}

export async function fetchEventHeats(
  base: string,
  eventId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<HeatOption[]> {
  const res = await fetchImpl(url(base, `/events/${eventId}/heats`))
  return (await check<{ heats: HeatOption[] }>(res, 'heats fetch')).heats
}

/** Canon heat-selection read (D15–D18). With { idToken, ticketId } the server
 *  scopes heats to the caller's ticket type (FULL ones included) and returns
 *  header context (event date, type name, current assignment). Without it,
 *  falls back to the legacy public unscoped list (context: null). */
export async function fetchHeatCatalog(
  base: string,
  eventId: string,
  opts?: { idToken: string; ticketId: string },
  fetchImpl: typeof fetch = fetch,
): Promise<HeatCatalog> {
  if (!opts) {
    const res = await fetchImpl(url(base, `/events/${eventId}/heats`))
    const { heats } = await check<{ heats: HeatOption[] }>(res, 'heats fetch')
    return { heats, context: null }
  }
  const res = await fetchImpl(
    url(base, `/events/${eventId}/heats?ticketId=${encodeURIComponent(opts.ticketId)}`),
    { headers: { Authorization: `Bearer ${opts.idToken}` } },
  )
  const body = await check<{ heats: HeatOption[]; context?: HeatListContext | null }>(res, 'heats fetch')
  return { heats: body.heats, context: body.context ?? null }
}

export async function assignHeat(
  idToken: string,
  base: string,
  ticketId: string,
  heatId: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<Readiness> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/heat`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ heatId }),
  })
  return check(res, 'heat assign')
}

/**
 * Bare-token QR (BOARDROOM-2026-08-24-192147 GO; design note
 * MY-2026-08-24-180947): ONE authed GET returns the caller's own ticket's
 * stable scan token (`tickets.anonymous_scan_token`) — THE QR payload,
 * rendered directly. Replaces the retired mintQr/fetchActiveQr pair (the
 * server keeps `qr/mint` alive only for the previously-deployed FE). Server
 * guard = live holder + the same readiness gate as the old mint (409
 * not_ready before get-ready completes); `token:null` = the token is not
 * available on this environment (honest absence, never fabricated).
 */
export async function fetchScanToken(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ token: string | null }> {
  const res = await fetchImpl(url(base, `/me/tickets/${ticketId}/scan-token`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'scan token fetch')
}

/** PURCHASER-scoped scan-token twin (my-api PR#125; Duncan item-5 amendment
 *  BOARDROOM-2026-09-02-152856): the buyer wallet's QR payload for a seat
 *  DEFERRED to the child's own parent. The holder-scoped fetchScanToken above
 *  is myLiveHolder-gated and 404s for the buyer — this order-addressed route
 *  serves the SAME {token} shape behind the server's signing-complete gate
 *  (the same gate that drives the allocations `qrAvailable` flag: the token
 *  serves ⇔ qrAvailable===true). Used ONLY for deferred-admitted wallet
 *  stubs; the buyer's own/held seats keep the holder path. */
export async function fetchPurchaserScanToken(
  idToken: string,
  base: string,
  orderId: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ token: string | null }> {
  const res = await fetchImpl(url(base, `/me/orders/${orderId}/tickets/${ticketId}/scan-token`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'purchaser scan token fetch')
}

// ---------------------------------------------------------------------------
// Event questionnaire — my-api BFF over settings' /svc seam
// (SETTINGS-2026-08-25-105204; agreed contract MY-2026-08-25-054534: the BFF
// proxies server-side with the shared service token — the FE NEVER holds it).
// Shapes are the settings pass-through (snake_case on the wire, unreshaped).
// ---------------------------------------------------------------------------

/** One selectable option — CONTRACT (amends the PR#310 note; ratified both
 *  sides: MY-2026-09-01-194520 pin + SETTINGS-2026-09-01-200559 confirm):
 *  `label_i18n` (Record locale→string) is the CANONICAL label shape, resolved
 *  CLIENT-side at render time (locale lives in the client; a server-flat
 *  label would bake one locale into the wire). Flat `label` is LEGACY
 *  optional — wins when non-empty (settings' v1 fitness rows {label,value}
 *  still carry it). Renderer resolution: label → label_i18n[locale] →
 *  label_i18n.en → first value → String(value). */
export interface QuestionnaireOption {
  value: string | number
  label?: string | null
  label_i18n?: Record<string, string> | null
}

export interface QuestionnaireQuestion {
  question_id: string
  prompt: string
  qtype: string
  /** Option set for choice questions; null/absent → free-text fallback. */
  options?: QuestionnaireOption[] | null
  /** Settings PR#310: `true` → render the choice question as a type-ahead
   *  dropdown; absent/false → button/checkbox choices. There is NO list-length
   *  heuristic. Tolerated-absent so older payloads (no field) keep rendering
   *  as buttons — EXCEPT that the LIVE resolved wire (BOARDROOM-2026-09-01-
   *  183926 probe) still self-describes dropdowns via legacy qtype:"dropdown"
   *  with no searchable field at all, so the renderer also honours that
   *  declaration (see isSearchable in QuestionnaireFlow). */
  searchable?: boolean
  required: boolean
  /** Settings #471/#630: per-question "this participant has a live answer"
   *  flag — the ONLY answered signal (questionAnswered). Absent ⇒ NOT
   *  answered; the resolved-level `submitted` is never a fallback. For the
   *  fitness_self row it reflects the participant's LATEST fitness answer on
   *  ANY event (#630). */
  answered?: boolean
  /** Settings #630: the stored answer (jsonb), present only when answered. */
  answer_value?: unknown
}

/** BFF read result. The get-ready step renders IFF active && show and at
 *  least one NON-fitness question exists; it is done only when every REQUIRED
 *  non-fitness question is answered per-question (questionnaireComplete —
 *  never `submitted`, which is some(answered) incl. a carried fitness
 *  answer); !active or !show → step absent entirely. */
export interface QuestionnaireStatus {
  active: boolean
  show: boolean
  submitted: boolean
  questions: QuestionnaireQuestion[]
}

/** CHILD-SUBJECT ticket context (boardroom ruling 2026-10-10): a questionnaire
 *  opened for a CHILD seat (kind='child' or a guardian claim) passes that
 *  seat's ticket so the BFF keys the read/write on the child
 *  (subject_holder_id), never the guardian's account. Absent ⇒ the request is
 *  byte-identical to the account read/write. The server flag decides whether
 *  the ticket is honoured; the FE may always send it for child entries. */
export interface QuestionnaireSubject {
  ticketId?: string
}

export async function fetchQuestionnaire(
  idToken: string,
  base: string,
  eventId: string,
  fetchImpl: typeof fetch = fetch,
  subject?: QuestionnaireSubject,
): Promise<QuestionnaireStatus> {
  const qs = subject?.ticketId ? `?ticket_id=${encodeURIComponent(subject.ticketId)}` : ''
  const res = await fetchImpl(url(base, `/me/events/${encodeURIComponent(eventId)}/questionnaire${qs}`), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'questionnaire fetch')
}

/** Records ONE answer via the BFF (POST …/questionnaire/answer). The server
 *  keys it on the caller's verified Cognito sub — the FE sends no identity.
 *  answer_value is the V62 OBJECT shape `{value: …}` — settings'
 *  questionnaireSvc rejects a bare string/number with a 400 (Duncan-hit live,
 *  BOARDROOM-2026-08-28-164014: every fitness submit failed). The caller
 *  passes the option's resolved score (fitness: 3|2|1) or the free-text
 *  string; the wrapping lives HERE so no call site can regress to bare.
 *
 *  MULTI-SELECT (answer-shape v1, CONFIRMED by SETTINGS-2026-08-31-183042): a
 *  multiple_choice answer passes an ARRAY of the selected option values,
 *  serialised as `answer_value: { value: [v1, v2, …] }` inside the same V62
 *  envelope the single-select path uses — single-select/free-text are
 *  byte-for-byte unchanged when no labels are supplied. Choice answers SHOULD
 *  also carry display labels (multi `labels:[…]`, single `label:"…"`) so the
 *  admin participants CSV prints display strings; absent labels degrade fine
 *  settings-side. */
export async function submitQuestionnaireAnswer(
  idToken: string,
  base: string,
  eventId: string,
  questionId: string,
  answerValue: string | number | Array<string | number>,
  // Display labels for choice answers (answer-shape v1 SHOULD, SETTINGS-2026-
  // 08-31-183042): multi → labels:[…], single → label:"…" — the admin CSV then
  // prints display strings. Absent (free text / older callers) degrades fine.
  labels?: string[],
  fetchImpl: typeof fetch = fetch,
  subject?: QuestionnaireSubject,
): Promise<{ answer_id: string; stored: boolean }> {
  const answer_value = Array.isArray(answerValue)
    ? { value: answerValue, ...(labels && labels.length > 0 ? { labels } : {}) }
    : { value: answerValue, ...(labels && labels.length === 1 ? { label: labels[0] } : {}) }
  const res = await fetchImpl(url(base, `/me/events/${encodeURIComponent(eventId)}/questionnaire/answer`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      question_id: questionId,
      answer_value,
      ...(subject?.ticketId ? { ticket_id: subject.ticketId } : {}),
    }),
  })
  return check(res, 'questionnaire answer')
}

/** CLEAR a previously stored OPTIONAL answer (prefill follow-up, boardroom
 *  review of #280). Settings stores answer_value `{}` and its reads count
 *  `{}` as NOT answered (questionnaireSvc ANSWER_LIVE_NON_EMPTY), so the
 *  question reads empty again. Only for optional questions — a required one
 *  can never be submitted empty. */
export async function clearQuestionnaireAnswer(
  idToken: string,
  base: string,
  eventId: string,
  questionId: string,
  fetchImpl: typeof fetch = fetch,
  subject?: QuestionnaireSubject,
): Promise<unknown> {
  const res = await fetchImpl(url(base, `/me/events/${encodeURIComponent(eventId)}/questionnaire/answer`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      question_id: questionId,
      answer_value: {},
      ...(subject?.ticketId ? { ticket_id: subject.ticketId } : {}),
    }),
  })
  return check(res, 'questionnaire answer clear')
}

export async function lookupClaim(
  base: string,
  inviteToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ClaimLookup> {
  const res = await fetchImpl(url(base, `/claim/${inviteToken}`))
  return check(res, 'claim lookup')
}

export async function claimInvite(
  idToken: string,
  base: string,
  inviteToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ClaimResult> {
  const res = await fetchImpl(url(base, `/claim/${inviteToken}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'claim')
}

/** PENDING INVITE (boardroom go 2026-10-08): an UNCLAIMED seat allocated to
 *  the signed-in account's VERIFIED email. The wire never carries the invite
 *  token or any email. */
export interface PendingInvite {
  holderId: string
  ticketId: string
  eventId: string
  eventName: string
  eventStartsAt?: string | null
  ticketTypeName: string
  invitedByName: string | null
  claimKind?: 'guardian'
  childName?: string
}

/** GET /me/pending-invites — throws on any failure (callers hide the section). */
export async function fetchPendingInvites(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PendingInvite[]> {
  const res = await fetchImpl(url(base, '/me/pending-invites'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  const body = await check<{ invites?: unknown }>(res, 'pending invites fetch')
  return Array.isArray(body.invites) ? (body.invites as PendingInvite[]) : []
}

/** POST /me/invites/{holderId}/accept — the SAME server claim as POST
 *  /claim/{token} (verified-email match), keyed by holder id. */
export async function acceptPendingInvite(
  idToken: string,
  base: string,
  holderId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ClaimResult> {
  const res = await fetchImpl(url(base, `/me/invites/${encodeURIComponent(holderId)}/accept`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check(res, 'invite accept')
}

/**
 * /claim smart-path (BOARDROOM-222954): provision the INVITED account (public,
 * keyed by the invite token — the invited email is resolved server-side, never
 * sent from the client). 'created' ⇒ a new account was made with `password`
 * (sign in with it); 'exists' ⇒ the account already exists (the entered password
 * is tried as the existing one). `email` is the full invited address to sign in.
 */
export async function createClaimAccount(
  base: string,
  inviteToken: string,
  password: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ outcome: 'created' | 'exists'; email: string }> {
  const res = await fetchImpl(url(base, `/claim/${inviteToken}/create-account`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  return check(res, 'create-account')
}

// ---------------------------------------------------------------------------
// Pending-claim continuity across the hosted-UI OAuth round trip: the claim
// token is stashed before redirecting to Cognito and consumed when the app
// boots with a session. localStorage (not sessionStorage) because signup can
// bounce through an email-verification step in a fresh tab.
// ---------------------------------------------------------------------------

const PENDING_CLAIM_KEY = 'my.pendingClaimToken'

export function storePendingClaim(token: string): void {
  localStorage.setItem(PENDING_CLAIM_KEY, token)
}

export function takePendingClaim(): string | null {
  const t = localStorage.getItem(PENDING_CLAIM_KEY)
  if (t) localStorage.removeItem(PENDING_CLAIM_KEY)
  return t
}

export function peekPendingClaim(): string | null {
  return localStorage.getItem(PENDING_CLAIM_KEY)
}

// ---------------------------------------------------------------------------
// Legacy marry-up pre-fill marker (CMT-035). The email-first login finishes
// with a full-page redirect, so the "open the profile wizard pre-filled from
// the legacy account" signal must survive the reload. We carry ONLY a boolean
// marker — NEVER the profile — so no PII lands in localStorage (nor any URL /
// query string). The wizard re-fetches the (idempotent) legacy profile via the
// authed endpoint when the marker is present, then clears it.
// ---------------------------------------------------------------------------

const LEGACY_PREFILL_KEY = 'my.legacyPrefillPending'

export function markLegacyPrefillPending(): void {
  localStorage.setItem(LEGACY_PREFILL_KEY, '1')
}

/** Read-and-clear the marker (one-shot). */
export function takeLegacyPrefillPending(): boolean {
  const v = localStorage.getItem(LEGACY_PREFILL_KEY)
  if (v) localStorage.removeItem(LEGACY_PREFILL_KEY)
  return v === '1'
}

// ---------------------------------------------------------------------------
// Brand signup + My newsletters (design MY-DESIGN-BRAND-SIGNUP-AND-COMPLETE-
// PROFILE-1020 §8, row _design/DESIGN-BRAND-SIGNUP-AND-NEWSLETTERS-V1; my-api
// PR-A #404, services/my-api/src/brand-signup.ts). Every route answers 404
// while my-api's MY_BRAND_SIGNUP_ENABLED is off; the FE side sits behind
// VITE_BRAND_SIGNUP_ENABLED (src/lib/brandSignupFlag.ts).
// ---------------------------------------------------------------------------

/** GET /join/{slug}/config — the brand form's copy (public). */
export interface JoinConfig {
  display_name: string
  opt_in_wording: string
  wording_version: string
}

export interface JoinSignupInput {
  email: string
  first_name: string
  last_name?: string
  opt_in: boolean
  adult_attested: boolean
  turnstile_token: string
  /** The honeypot field's value (empty for a person). */
  hp: string
}

/** GET /join/{slug}/config. 404 unknown/disabled brand, 503
 *  brand_config_unavailable — both throw MyApiError. */
export async function fetchJoinConfig(
  base: string,
  slug: string,
  fetchImpl: typeof fetch = fetch,
): Promise<JoinConfig> {
  const res = await fetchImpl(url(base, `/join/${encodeURIComponent(slug)}/config`))
  return check<JoinConfig>(res, 'join config')
}

/** POST /join/{slug}/signup (public). 202 {status:'check_email'} past
 *  validation, the same answer for a known or unknown email. Throws
 *  MyApiError: 400 invalid_email/invalid_input, 422 adult_attestation_required,
 *  403 turnstile_failed, 404 brand, 503 *_unavailable. */
export async function submitJoinSignup(
  base: string,
  slug: string,
  input: JoinSignupInput,
  fetchImpl: typeof fetch = fetch,
): Promise<{ status: 'check_email' }> {
  const res = await fetchImpl(url(base, `/join/${encodeURIComponent(slug)}/signup`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check<{ status: 'check_email' }>(res, 'join signup')
}

export interface JoinConfirmResult {
  org_unit_id: string
  display_name: string
  opted_in: boolean
}

/** POST /join/confirm {token} (authenticated). Throws MyApiError: 403
 *  email_mismatch, 410 link_expired, 409 already_confirmed. */
export async function confirmJoin(
  idToken: string,
  base: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<JoinConfirmResult> {
  const res = await fetchImpl(url(base, '/join/confirm'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })
  return check<JoinConfirmResult>(res, 'join confirm')
}

export type NewsletterState = 'subscribed' | 'unsubscribed' | 'never_asked'

/** One GET /me/newsletters row (marketing-service's merged state, passed
 *  through by the my-api BFF). */
export interface Newsletter {
  org_unit_id: string
  brand_name: string | null
  state: NewsletterState
  since: string | null
  source: string | null
  not_contactable: string[]
  editable: boolean
  reason?: 'contact_support' | 'parent_managed'
  pending?: boolean
}

export interface NewslettersRead {
  available: boolean
  newsletters: Newsletter[]
}

function parseNewsletter(raw: unknown): Newsletter | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  if (typeof o.org_unit_id !== 'string' || !o.org_unit_id) return null
  const state = o.state === 'subscribed' || o.state === 'unsubscribed' || o.state === 'never_asked' ? o.state : null
  if (!state) return null
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null)
  return {
    org_unit_id: o.org_unit_id,
    brand_name: str(o.brand_name),
    state,
    since: str(o.since),
    source: str(o.source),
    not_contactable: Array.isArray(o.not_contactable)
      ? (o.not_contactable as unknown[]).filter((r): r is string => typeof r === 'string')
      : [],
    // Fail closed: only an explicit true offers the switch.
    editable: o.editable === true,
    ...(o.reason === 'contact_support' || o.reason === 'parent_managed' ? { reason: o.reason } : {}),
    ...(o.pending === true ? { pending: true } : {}),
  }
}

/** DEFENSIVE parse of a GET /me/newsletters body: a malformed envelope reads
 *  as unavailable; a malformed row is dropped, never guessed. */
export function parseNewsletters(raw: unknown): NewslettersRead {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { available: false, newsletters: [] }
  const o = raw as Record<string, unknown>
  if (o.available !== true) return { available: false, newsletters: [] }
  const rows = Array.isArray(o.newsletters) ? o.newsletters : []
  return { available: true, newsletters: rows.map(parseNewsletter).filter((n): n is Newsletter => n !== null) }
}

/** GET /me/newsletters. HTTP failures throw MyApiError (404 = my-api's flag
 *  off); the hook decides the degrade. */
export async function fetchNewsletters(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<NewslettersRead> {
  const res = await fetchImpl(url(base, '/me/newsletters'), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return parseNewsletters(await check<unknown>(res, 'newsletters fetch'))
}

/** The wording version recorded with a My newsletters switch. The switch copy
 *  is this app's own (en.ts `newsletters.*`); bump this when it changes.
 *  The list read carries no brand wording, and my-api requires one. */
export const NEWSLETTERS_WORDING_VERSION = 'my-newsletters-v1'

/** PUT /me/newsletters/{org_unit_id} — sent as its POST alias (the API
 *  Gateway CORS allow-list carries GET/POST only, as for /me/profile).
 *  200 {org_unit_id, subscribed, pending:true}. Throws MyApiError: 403
 *  minor_parent_managed, 409 not_contactable / contact_support, 404
 *  org_unit_not_found. */
export async function setNewsletter(
  idToken: string,
  base: string,
  orgUnitId: string,
  subscribed: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<{ org_unit_id: string; subscribed: boolean; pending?: boolean }> {
  const res = await fetchImpl(url(base, `/me/newsletters/${encodeURIComponent(orgUnitId)}`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscribed, wording_version: NEWSLETTERS_WORDING_VERSION }),
  })
  return check(res, 'newsletter switch')
}

// Pending join-confirm continuity. A signed-out visitor on /join/confirm?t=…
// signs in through the native email-code flow, which returns to the same path
// (Login's returnTo). The token is ALSO stashed here, briefly, because the
// sign-in email's own link opens /sign-in?code=… in a fresh tab and a first
// sign-in can detour through the legacy profile review; the authed app resumes
// /join/confirm from the stash. localStorage, one hour, cleared on use. The
// token is single-use and only works for the signed-up email's account.

const PENDING_JOIN_KEY = 'my.pendingJoinConfirm'
const PENDING_JOIN_TTL_MS = 60 * 60 * 1000

export function storePendingJoinConfirm(token: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(PENDING_JOIN_KEY, JSON.stringify({ t: token, exp: now + PENDING_JOIN_TTL_MS }))
  } catch {
    /* storage unavailable — Login's returnTo still carries the path */
  }
}

/** Read-and-clear (one-shot). Null when absent, malformed or expired. */
export function takePendingJoinConfirm(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PENDING_JOIN_KEY)
    if (!raw) return null
    localStorage.removeItem(PENDING_JOIN_KEY)
    const v = JSON.parse(raw) as { t?: unknown; exp?: unknown }
    return typeof v.t === 'string' && v.t && typeof v.exp === 'number' && v.exp > now ? v.t : null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// "Complete your profile" links (design MY-DESIGN-BRAND-SIGNUP-AND-COMPLETE-
// PROFILE-1020, PR-C). A signed email link /complete-profile?t=… opens an
// authed page that offers the brand's newsletter and asks ONLY for what the
// profile is missing. Every route answers 404 while my-api's
// MY_PROFILE_LINKS_ENABLED is off; the FE sits behind
// VITE_PROFILE_LINKS_ENABLED (src/lib/profileLinksFlag.ts). The token goes
// in the path, encoded, and is never logged.
// ---------------------------------------------------------------------------

export interface ProfileLinkOption {
  value: string
  label: string
  label_i18n?: Record<string, string>
}

export interface ProfileLinkOrgField {
  definition_id: string
  key: string
  label: string
  label_i18n?: Record<string, string>
  type: 'single_choice' | 'multi_choice'
  required: boolean
  options: ProfileLinkOption[]
}

export interface ProfileLinkRead {
  org_unit_id: string
  brand_name: string
  /** null = marketing state unavailable (the Confirm button still shows). */
  subscribed: NewsletterState | null
  missing: { city: boolean; org_fields: ProfileLinkOrgField[] }
  wording_version: string
}

function parseI18n(raw: unknown): Record<string, string> | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim()) out[k] = v
  }
  return Object.keys(out).length ? out : undefined
}

function parseOption(raw: unknown): ProfileLinkOption | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  if (typeof o.value !== 'string' || !o.value || typeof o.label !== 'string' || !o.label.trim()) return null
  const i18n = parseI18n(o.label_i18n)
  return { value: o.value, label: o.label, ...(i18n ? { label_i18n: i18n } : {}) }
}

function parseOrgField(raw: unknown): ProfileLinkOrgField | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  if (typeof o.definition_id !== 'string' || !o.definition_id) return null
  if (typeof o.key !== 'string' || typeof o.label !== 'string' || !o.label.trim()) return null
  if (o.type !== 'single_choice' && o.type !== 'multi_choice') return null
  const options = Array.isArray(o.options) ? o.options.map(parseOption).filter((x): x is ProfileLinkOption => x !== null) : []
  // A choice question with nothing to choose can't be answered — drop it.
  if (options.length === 0) return null
  const i18n = parseI18n(o.label_i18n)
  return {
    definition_id: o.definition_id,
    key: o.key,
    label: o.label,
    ...(i18n ? { label_i18n: i18n } : {}),
    type: o.type,
    required: o.required === true,
    options,
  }
}

/** DEFENSIVE parse of a GET /me/profile-links/{token} body. A malformed
 *  envelope throws (the page shows its retry state); a malformed org field is
 *  dropped, never guessed. */
export function parseProfileLink(raw: unknown): ProfileLinkRead {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) throw new MyApiError('profile link: malformed body', 0, 'malformed')
  const o = raw as Record<string, unknown>
  if (typeof o.org_unit_id !== 'string' || !o.org_unit_id || typeof o.wording_version !== 'string' || !o.wording_version) {
    throw new MyApiError('profile link: malformed body', 0, 'malformed')
  }
  const subscribed = o.subscribed === 'subscribed' || o.subscribed === 'unsubscribed' || o.subscribed === 'never_asked' ? o.subscribed : null
  const m = typeof o.missing === 'object' && o.missing !== null && !Array.isArray(o.missing) ? (o.missing as Record<string, unknown>) : {}
  const fields = Array.isArray(m.org_fields) ? m.org_fields.map(parseOrgField).filter((f): f is ProfileLinkOrgField => f !== null) : []
  return {
    org_unit_id: o.org_unit_id,
    brand_name: typeof o.brand_name === 'string' ? o.brand_name : '',
    subscribed,
    // Fail closed: only an explicit true asks for the city.
    missing: { city: m.city === true, org_fields: fields },
    wording_version: o.wording_version,
  }
}

function profileLinkPath(token: string, rest = ''): string {
  return `/me/profile-links/${encodeURIComponent(token)}${rest}`
}

/** GET /me/profile-links/{token}. Throws MyApiError: 410 link_expired, 403
 *  wrong_account, 404 not_found (my-api flag off). */
export async function fetchProfileLink(
  idToken: string,
  base: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ProfileLinkRead> {
  const res = await fetchImpl(url(base, profileLinkPath(token)), {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return parseProfileLink(await check<unknown>(res, 'profile link fetch'))
}

/** POST /me/profile-links/{token}/subscribe {wording_version} → 200
 *  {subscribed:true, pending:true}. Throws MyApiError: 403
 *  minor_parent_managed / wrong_account, 410 link_expired. */
export async function subscribeViaProfileLink(
  idToken: string,
  base: string,
  token: string,
  wordingVersion: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ subscribed: true; pending?: boolean }> {
  const res = await fetchImpl(url(base, profileLinkPath(token, '/subscribe')), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ wording_version: wordingVersion }),
  })
  return check(res, 'profile link subscribe')
}

/** PUT /me/profile-links/{token}/org-fields/{definitionId} — sent as its POST
 *  alias (the API Gateway CORS allow-list carries GET/POST only, as for
 *  /me/newsletters). value: string (single_choice) or string[]
 *  (multi_choice). 200 {saved:true}. Throws MyApiError: 409
 *  already_answered, 403, 410. */
export async function saveProfileLinkOrgField(
  idToken: string,
  base: string,
  token: string,
  definitionId: string,
  value: string | string[],
  fetchImpl: typeof fetch = fetch,
): Promise<{ saved: true }> {
  const res = await fetchImpl(url(base, profileLinkPath(token, `/org-fields/${encodeURIComponent(definitionId)}`)), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  })
  return check(res, 'profile link org field')
}

// Pending profile-link continuity — the same shape as the join-confirm stash
// above (own key, one hour, one-shot): a signed-out visitor signs in at the
// same path (Login's returnTo), and the stash covers the fresh-tab sign-in
// link / first-sign-in detours.

const PENDING_PROFILE_LINK_KEY = 'my.pendingProfileLink'
const PENDING_PROFILE_LINK_TTL_MS = 60 * 60 * 1000

export function storePendingProfileLink(token: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(PENDING_PROFILE_LINK_KEY, JSON.stringify({ t: token, exp: now + PENDING_PROFILE_LINK_TTL_MS }))
  } catch {
    /* storage unavailable — Login's returnTo still carries the path */
  }
}

/** Read-and-clear (one-shot). Null when absent, malformed or expired. */
export function takePendingProfileLink(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PENDING_PROFILE_LINK_KEY)
    if (!raw) return null
    localStorage.removeItem(PENDING_PROFILE_LINK_KEY)
    const v = JSON.parse(raw) as { t?: unknown; exp?: unknown }
    return typeof v.t === 'string' && v.t && typeof v.exp === 'number' && v.exp > now ? v.t : null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// TEEN SELF-HOLDER (PR3 FE; my-api PR1 + PR2 routes; design MY-DESIGN-JUNIOR-
// SELF-HOLDER-1020 §4.1; PROGRAMME-RULE-TEEN-SELF-HOLDER-TRANSACTIONAL-EMAIL-
// V1). Called only behind VITE_TEEN_SELF_HOLDER_ENABLED. The guardian email is
// sent, never echoed back, and never logged or stored by these helpers.
// ---------------------------------------------------------------------------

export type GuardianRequestState = 'none' | 'requested' | 'verified' | 'signed' | 'expired' | 'superseded' | 'revoked'

/** What the request routes return (ids, state and expiry only). my-api's
 *  read of the latest request (GET /me/tickets/{id}/guardian-request and
 *  readiness `guardianRequest` on a minor self row — additive follow-up after
 *  #418) carries the same three fields plus the optional ones below; never
 *  the parent's email. requestId is null there when state is 'none'. */
export interface GuardianRequestView {
  requestId: string | null
  state: GuardianRequestState
  expiresAt: string
  /** The parent's name as the teen typed it (server read only). */
  guardianName?: string | null
  requestedAt?: string | null
  changeCount?: number
  changesLeft?: number
  /** Live request only: when Resend is next allowed (null = now). */
  canResendAt?: string | null
}

export interface GuardianRequestInput {
  guardianName: string
  guardianEmail: string
  relationship: string
}

/** POST /me/tickets/{id}/guardian-request → 201 {requestId, state, expiresAt}. */
export async function requestGuardianSignature(
  idToken: string,
  base: string,
  ticketId: string,
  input: GuardianRequestInput,
  fetchImpl: typeof fetch = fetch,
): Promise<GuardianRequestView> {
  const res = await fetchImpl(url(base, `/me/tickets/${encodeURIComponent(ticketId)}/guardian-request`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check<GuardianRequestView>(res, 'guardian request')
}

/** PUT /me/tickets/{id}/guardian-request — change the parent's email before
 *  signing (supersedes the live request; 3 changes max). */
export async function changeGuardianRequest(
  idToken: string,
  base: string,
  ticketId: string,
  input: GuardianRequestInput,
  fetchImpl: typeof fetch = fetch,
): Promise<GuardianRequestView> {
  const res = await fetchImpl(url(base, `/me/tickets/${encodeURIComponent(ticketId)}/guardian-request`), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return check<GuardianRequestView>(res, 'guardian request change')
}

/** POST /me/tickets/{id}/guardian-request/resend (one send per 24 h). */
export async function resendGuardianRequest(
  idToken: string,
  base: string,
  ticketId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GuardianRequestView> {
  const res = await fetchImpl(url(base, `/me/tickets/${encodeURIComponent(ticketId)}/guardian-request/resend`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  return check<GuardianRequestView>(res, 'guardian request resend')
}

// ---- public parent sign page (/guardian-sign/{token}; PR2) -----------------

/** GET /guardian-sign/{token} — the teen's display name, event, ticket type,
 *  race date, state and event_id; no contact data. `event_id` keys the
 *  waiver-template read (made after verify step 2; my-api sends it on GET and
 *  on both verify replies since the follow-up after #418). */
export interface GuardianSignView {
  child_display_name: string | null
  event_name: string
  ticket_type: string | null
  race_date: string | null
  state: string
  event_id?: string | null
}

const guardianSignPath = (token: string, suffix = ''): string => `/guardian-sign/${encodeURIComponent(token)}${suffix}`

export async function fetchGuardianSign(base: string, token: string, fetchImpl: typeof fetch = fetch): Promise<GuardianSignView> {
  const res = await fetchImpl(url(base, guardianSignPath(token)), { referrerPolicy: 'no-referrer' })
  return check<GuardianSignView>(res, 'guardian sign view')
}

/** Verify step 1 (no Authorization): the parent's typed email must equal the
 *  stored one; my-api prepares their account → 202 {next:'email_code'}. */
export async function guardianSignVerifyEmail(
  base: string,
  token: string,
  email: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ next: string }> {
  const res = await fetchImpl(url(base, guardianSignPath(token, '/verify')), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
    referrerPolicy: 'no-referrer',
  })
  return check<{ next: string }>(res, 'guardian sign verify')
}

/** Verify step 2: the ID token from the native email-code sign-in → a
 *  30-minute signing session. */
export interface GuardianSignSession {
  state: string
  session: string
  session_expires_at: string
  event_id?: string | null
}

export async function guardianSignVerifySession(
  base: string,
  token: string,
  idToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GuardianSignSession> {
  const res = await fetchImpl(url(base, guardianSignPath(token, '/verify')), {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
    referrerPolicy: 'no-referrer',
  })
  return check<GuardianSignSession>(res, 'guardian sign session')
}

export interface GuardianSignInput {
  session: string
  typedName: string
  relationship: string
  declaresAdult: true
  accepted_sections: string[]
  /** Box (a) — the child may receive news of similar {brand} events. */
  consent_child_news: boolean
  /** Box (b) — the parent may receive news of similar {brand} events. */
  consent_parent_news: boolean
}

export async function guardianSignSubmit(
  base: string,
  token: string,
  input: GuardianSignInput,
  fetchImpl: typeof fetch = fetch,
): Promise<{ state: string; signed_at: string }> {
  const res = await fetchImpl(url(base, guardianSignPath(token, '/sign')), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    referrerPolicy: 'no-referrer',
  })
  return check<{ state: string; signed_at: string }>(res, 'guardian sign')
}

// Pending guardian-sign continuity — the join-confirm stash shape (own key,
// one hour): set after verify step 1 so the page resumes verify step 2 once
// the native email-code sign-in returns (Login's returnTo), or after a
// fresh-tab / first-sign-in detour (App.tsx resumes it). The token is never
// logged.

const PENDING_GUARDIAN_SIGN_KEY = 'my.pendingGuardianSign'
const PENDING_GUARDIAN_SIGN_TTL_MS = 60 * 60 * 1000

export function storePendingGuardianSign(token: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(PENDING_GUARDIAN_SIGN_KEY, JSON.stringify({ t: token, exp: now + PENDING_GUARDIAN_SIGN_TTL_MS }))
  } catch {
    /* storage unavailable — Login's returnTo still carries the path */
  }
}

/** Read WITHOUT clearing. Null when absent, malformed or expired. */
export function peekPendingGuardianSign(now: number = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(PENDING_GUARDIAN_SIGN_KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as { t?: unknown; exp?: unknown }
    return typeof v.t === 'string' && v.t && typeof v.exp === 'number' && v.exp > now ? v.t : null
  } catch {
    return null
  }
}

/** Read-and-clear (one-shot). */
export function takePendingGuardianSign(now: number = Date.now()): string | null {
  const t = peekPendingGuardianSign(now)
  try {
    localStorage.removeItem(PENDING_GUARDIAN_SIGN_KEY)
  } catch {
    /* no-op */
  }
  return t
}

/** GET /me/orders/unassigned-summary → how many seats the caller still has to
 *  assign on paid, live orders for events not yet ended (my-api #431). Drives
 *  the post-sign-in home: Orders first when > 0. Throws MyApiError on any
 *  non-2xx (an older my-api answers 404); callers fall back to Get ready. */
export async function fetchUnassignedSeats(
  idToken: string,
  base: string,
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<number> {
  const res = await fetchImpl(url(base, '/me/orders/unassigned-summary'), {
    headers: { Authorization: `Bearer ${idToken}` },
    ...(signal ? { signal } : {}),
  })
  const body = await check<{ unassigned_seats?: unknown }>(res, 'unassigned summary')
  const n = body?.unassigned_seats
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}
